/**
 * GpuTelemetrySection
 *
 * Right-column content for the StreamingDashboardHeader GPU reveal.
 * Compact pill-style range selector, play/pause + Y-axis lock toggles,
 * live stats, and an 84px engine utilization chart.
 *
 * Smooth reveal animation via max-h + opacity transitions.
 */

import { useRef, useState } from "react";
import type ReactECharts from "echarts-for-react";
import { Icon } from "../ui/Icon";
import { Tooltip } from "../ui/Tooltip";
import { Popover } from "../ui/Popover";
import { GpuTelemetryChart } from "./GpuTelemetryChart";
import type { GpuSample, GpuStreamStatus } from "../../hooks/useIntelGpuStream";
import { cn } from "../../utils/cn";

// ---------------------------------------------------------------------------
// Legend colors (must match GpuTelemetryChart ENGINE_COLORS)
// ---------------------------------------------------------------------------

const ENGINE_LEGEND = [
  { name: "Render", key: "render", color: "#34d399" },
  { name: "Video", key: "video", color: "#60a5fa" },
  { name: "Copy", key: "copy", color: "#a78bfa" },
  { name: "Freq", key: "freq", color: "#f59e0b" },
] as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type GpuTimeseriesRange = "5m" | "15m" | "1h" | "6h" | "24h";

export type GpuTelemetrySectionProps = {
  expanded: boolean;
  range: GpuTimeseriesRange;
  onRangeChange: (range: GpuTimeseriesRange) => void;
  mode: "live" | "paused";
  onToggleMode: () => void;
  latest: GpuSample | null;
  history: GpuSample[];
  status: GpuStreamStatus;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RANGES: GpuTimeseriesRange[] = ["5m", "15m", "1h", "6h", "24h"];

const CHART_HEIGHT = 84;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatFreq = (mhz: number | null): string => {
  if (mhz === null) return "--";
  return `${Math.round(mhz)} MHz`;
};

const formatTemp = (c: number | null): string => {
  if (c === null) return "--";
  return `${c.toFixed(0)}\u00B0C`;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GpuTelemetrySection = ({
  expanded,
  range,
  onRangeChange,
  mode,
  onToggleMode,
  latest,
  history,
  status,
}: GpuTelemetrySectionProps) => {
  const [yAxisLocked, setYAxisLocked] = useState(false);
  const gpuChartRef = useRef<ReactECharts>(null);

  const isLive = status === "live";

  // Zoom control handlers
  const dispatchZoom = (start: number, end: number) => {
    gpuChartRef.current?.getEchartsInstance()?.dispatchAction({
      type: "dataZoom",
      start: Math.max(0, start),
      end: Math.min(100, end),
    });
  };

  const handleZoomIn = () => {
    const instance = gpuChartRef.current?.getEchartsInstance();
    const opt = instance?.getOption();
    const dz = (opt?.dataZoom as Array<{ start?: number; end?: number }>)?.[0];
    const start = dz?.start ?? 0;
    const end = dz?.end ?? 100;
    const range = end - start;
    const newRange = Math.max(10, range - 20);
    const center = (start + end) / 2;
    dispatchZoom(center - newRange / 2, center + newRange / 2);
  };

  const handleZoomOut = () => {
    const instance = gpuChartRef.current?.getEchartsInstance();
    const opt = instance?.getOption();
    const dz = (opt?.dataZoom as Array<{ start?: number; end?: number }>)?.[0];
    const start = dz?.start ?? 0;
    const end = dz?.end ?? 100;
    const range = end - start;
    const newRange = Math.min(100, range + 20);
    const center = (start + end) / 2;
    dispatchZoom(center - newRange / 2, center + newRange / 2);
  };

  const handleZoomReset = () => {
    dispatchZoom(0, 100);
  };
  const hasData = isLive || (status === "connecting" && history.length > 0);

  return (
    <div
      role="region"
      aria-label="GPU Telemetry"
      className={cn(
        "transition-all duration-300 ease-in-out overflow-hidden",
        expanded ? "max-h-[180px] opacity-100" : "max-h-0 opacity-0",
      )}
    >
      {/* Panel container */}
      <div className="border border-border/40 rounded-lg bg-card/30 overflow-hidden">
        {/* Panel header */}
        <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border/30">
          {/* Compact range pills */}
          <div
            role="tablist"
            aria-label="GPU telemetry time range"
            className="flex items-center gap-0.5"
          >
            {RANGES.map((r) => (
              <button
                key={r}
                type="button"
                role="tab"
                aria-selected={range === r}
                tabIndex={range === r ? 0 : -1}
                onClick={() => onRangeChange(r)}
                className={cn(
                  "px-1.5 py-0.5 text-[10px] font-medium rounded transition-colors",
                  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/70",
                  range === r
                    ? "bg-purple-500/20 text-purple-400"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {r}
              </button>
            ))}
          </div>

          {/* Tiny icon buttons: play/pause + lock + legend */}
          <div className="flex items-center gap-0.5">
            <Tooltip content={mode === "live" ? "Pause" : "Resume live"}>
              <button
                type="button"
                onClick={onToggleMode}
                aria-label={mode === "live" ? "Pause GPU telemetry" : "Resume GPU telemetry"}
                className={cn(
                  "p-1 rounded transition-colors",
                  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/70",
                  "text-muted-foreground hover:text-foreground",
                )}
              >
                <Icon
                  icon={mode === "live" ? "lucide:pause" : "lucide:play"}
                  className="h-3 w-3"
                  aria-hidden
                />
              </button>
            </Tooltip>
            <Tooltip content={yAxisLocked ? "Unlock Y-axis (dynamic scale)" : "Lock Y-axis at 100%"}>
              <button
                type="button"
                onClick={() => setYAxisLocked((prev) => !prev)}
                aria-label={yAxisLocked ? "Unlock Y-axis to dynamic scale" : "Lock Y-axis at 100%"}
                aria-pressed={yAxisLocked}
                className={cn(
                  "p-1 rounded transition-colors",
                  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/70",
                  yAxisLocked
                    ? "text-purple-400"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <Icon
                  icon={yAxisLocked ? "lucide:lock" : "lucide:unlock"}
                  className="h-3 w-3"
                  aria-hidden
                />
              </button>
            </Tooltip>
            <Tooltip content="Chart legend">
              <Popover
                side="bottom"
                align="start"
                disableMobileModal
                trigger={
                  <span
                    className={cn(
                      "p-1 rounded transition-colors cursor-pointer",
                      "text-muted-foreground hover:text-foreground",
                    )}
                    aria-label="Show chart legend"
                  >
                    <Icon icon="lucide:list" className="h-3 w-3" aria-hidden />
                  </span>
                }
                content={
                  <div className="px-2.5 py-2 space-y-1.5">
                    {ENGINE_LEGEND.map((item) => {
                      const isFreq = item.key === "freq";
                      const raw = isFreq
                        ? latest?.freqActMhz
                        : latest?.engines[item.key as keyof typeof latest.engines];
                      const display = raw != null
                        ? isFreq
                          ? `${Math.round(raw)} MHz`
                          : `${raw.toFixed(1)}%`
                        : "—";
                      return (
                        <div key={item.name} className="flex items-center gap-2 text-[11px]">
                          <span
                            className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ backgroundColor: item.color }}
                          />
                          <span className="text-foreground flex-1">{item.name}</span>
                          <span className="text-muted-foreground tabular-nums">
                            {display}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                }
              />
            </Tooltip>

            {/* Zoom controls */}
            <Tooltip content="Zoom in">
              <button
                type="button"
                onClick={handleZoomIn}
                className={cn(
                  "p-1 rounded transition-colors",
                  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/70",
                  "text-muted-foreground hover:text-foreground",
                )}
                aria-label="Zoom in"
              >
                <Icon icon="lucide:zoom-in" className="h-3 w-3" aria-hidden />
              </button>
            </Tooltip>
            <Tooltip content="Zoom out">
              <button
                type="button"
                onClick={handleZoomOut}
                className={cn(
                  "p-1 rounded transition-colors",
                  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/70",
                  "text-muted-foreground hover:text-foreground",
                )}
                aria-label="Zoom out"
              >
                <Icon icon="lucide:zoom-out" className="h-3 w-3" aria-hidden />
              </button>
            </Tooltip>
            <Tooltip content="Reset zoom">
              <button
                type="button"
                onClick={handleZoomReset}
                className={cn(
                  "p-1 rounded transition-colors",
                  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/70",
                  "text-muted-foreground hover:text-foreground",
                )}
                aria-label="Reset zoom"
              >
                <Icon icon="lucide:maximize-2" className="h-3 w-3" aria-hidden />
              </button>
            </Tooltip>
          </div>

          {/* Live stats (right-aligned) */}
          {latest && (
            <div className="ml-auto flex items-center gap-2 text-[10px] tabular-nums text-muted-foreground">
              {latest.freqActMhz !== null && (
                <span>
                  <span className="text-muted-foreground/60">Freq</span>{" "}
                  <span className="text-foreground">{formatFreq(latest.freqActMhz)}</span>
                </span>
              )}
              {latest.tempCelsius !== null && (
                <span>
                  <span className="text-muted-foreground/60">Temp</span>{" "}
                  <span className="text-foreground">{formatTemp(latest.tempCelsius)}</span>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Panel body - chart */}
        <div className="px-1 pb-1">
          {hasData ? (
            <GpuTelemetryChart
              ref={gpuChartRef}
              history={history}
              height={CHART_HEIGHT}
              yAxisLocked={yAxisLocked}
              freqMaxMhz={latest?.freqMaxMhz ?? null}
            />
          ) : status === "connecting" ? (
            <div className="flex items-center justify-center h-[84px] text-[10px] text-muted-foreground">
              <Icon icon="lucide:loader-2" className="h-3 w-3 animate-spin mr-1.5" aria-hidden />
              Connecting to GPU stream...
            </div>
          ) : status === "unavailable" ? (
            <div className="flex items-center justify-center h-[84px] text-[10px] text-muted-foreground">
              <Icon icon="lucide:cpu" className="h-3 w-3 mr-1.5" aria-hidden />
              No Intel iGPU telemetry available
            </div>
          ) : status === "error" ? (
            <div className="flex items-center justify-center h-[84px] text-[10px] text-destructive">
              <Icon icon="lucide:alert-triangle" className="h-3 w-3 mr-1.5" aria-hidden />
              GPU stream error
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
